import immer from 'immer';
import type { ReactiveVar } from '@apollo/client';
import { makeVar } from '@apollo/client';
import type { ReactiveStorage } from './graphqlClientStorage';
import { graphqlClientStorage } from './graphqlClientStorage';

export class ReactiveState<
  T extends S extends keyof ReactiveStorage ? ReactiveStorage[S] : unknown,
  S extends keyof ReactiveStorage | undefined = undefined,
> {
  private reactiveVar: ReactiveVar<T>;

  private storageKey: string;

  /**
   * A wrapper for Apollo's ReactiveVar
   * provides utility functions for CRUD operations
   * @param initialState initialize the var
   * @param storageKey if provided, the var will be saved to storage with this key
   * ⚠️ if using `storageKey`, you MUST define the shape of what's stored in `graphqlClientStorage`
   */
  constructor(initialState?: T, storageKey?: S) {
    if (storageKey) {
      this.storageKey = storageKey as string;
      const existingStorage = this.getStorage();
      if (existingStorage) this.makeVar(existingStorage);
      else if (initialState !== undefined) {
        this.setStorage(initialState as T);
        this.makeVar(initialState as T);
      }
    } else {
      this.makeVar(initialState as T);
    }
  }

  private makeVar(initialState?: T): T {
    this.reactiveVar = makeVar(initialState ?? null) as ReactiveVar<T>;
    return this.reactiveVar();
  }

  private setStorage(val: T) {
    if (this.storageKey) {
      graphqlClientStorage.set(`reactive-state-${this.storageKey}`, {
        [this.storageKey]: val,
      });
    }
  }

  private getStorage(): T | null {
    if (this.storageKey) {
      return graphqlClientStorage.get(`reactive-state-${this.storageKey}`, {})[
        this.storageKey
      ] as T;
    }
    return null;
  }

  /**
   * set a new state
   * @param newState the full new state
   */
  set(newState: T): Readonly<T>;

  /**
   * update the state from a previous value
   * @param produce immer produce function which returns the updated
   * @see produce docs
   */
  set(produce: (draft: T) => T): Readonly<T>;

  /**
   * update the state from a previous value
   * @param produce immer produce function which mutates the state
   * @see produce docs
   */
  set(produce: (draft: T) => void): Readonly<T>;

  set(value: T | ((draft: T) => any)): Readonly<T> {
    if (typeof value === 'function') {
      const newValue = immer(this.reactiveVar(), value);
      this.setStorage(newValue as T);
      return this.reactiveVar(newValue as T);
    }
    const val = this.reactiveVar(value);
    this.setStorage(val);
    return val;
  }

  /**
   * get and optionally init the state
   * @param initState if the state hasn't been initialized, do so with this value
   */
  get(initState?: T) {
    if (!this.reactiveVar) this.makeVar(initState ?? null);
    return this.reactiveVar();
  }

  /**
   * clears local storage if it exists
   */
  clearStorage() {
    if (this.storageKey)
      graphqlClientStorage.set(`reactive-state-${this.storageKey}`, {});
  }

  /**
   * clears local storage and nullifies the reactive var
   * @warn this will trigger a cache update
   */
  clear() {
    this.set(null as unknown as T);
    this.clearStorage();
  }

  getVar() {
    return this.reactiveVar;
  }
}
